import React, { useState, useCallback, useMemo, useEffect } from 'react'
import Accordion from '_/components/accordion'
import { useSelector } from 'react-redux'

import { TextField, Grid, Typography, Tooltip } from '@mui/material'
import { BasicInput, MaskBasicInput, Select } from '_/components/inputs'
import SwitchComponent from '_/components/switch'

import Trash from '_assets/icons/ic-trash.svg'
import IconButton from '_components/svg/icon-button'
import { useFormContext, Controller, useForm } from 'react-hook-form'
import Button from '_components/button'
import { getBuildingTypesSelector } from '_/modules/agency/selectors'
import { getCitiesOptions, getStatesOptionsByAcronyms } from '_/modules/utils/selectors'
import { BUSINESS_TYPE_OPTIONS } from '../../components/AgencyReferaDetailsModal/constants'
import { ADDRESS_FIELDS, MENU_PROPS } from './constants'
import { EMAIL_REGEX, PHONE_REGEX, CPF_CNPJ_REGEX } from '_/utils/constants'
import UnitModal from '../units'
import { useDialog } from '_/hooks/use-dialog'
import useCepAutoComplete from '_hooks/use-cep-autocomplete'

import classnames from 'classnames'
import { validationCep } from '_/utils/helpers'
import useStyles from '../styles'
import { useParams } from '@reach/router'
import { useMessage } from '_/hooks/use-message'
import HelpOutlineOutlinedIcon from '@material-ui/icons/HelpOutlineOutlined'
import { ControlledCpfCnpjInput } from '_/components/inputs/MaskInput/common/CpfCnpjInput'

export const { CEP, ADDRESS, NUMBER, COMPLEMENT, NEIGHBORHOOD, STATE, CITY } = ADDRESS_FIELDS

export const BuildingData = ({ isEditing }) => {
  const styles = useStyles()
  const buildingTypes = useSelector(getBuildingTypesSelector)
  const { getValues } = useFormContext()
  const { control } = useFormContext()

  return (
    <Accordion title="Dados principais" className={styles.accordion}>
      <div className={styles.column}>
        <div className={styles.row}>
          <BasicInput
            name="name"
            label="Nome"
            labelClasses={styles.radioLabel}
            required
            disabled={!isEditing}
            defaultValue={getValues('name')}
            rules={{
              required: { value: true, message: 'Esse campo é obrigatório' },
              maxLength: {
                value: 250,
                message: `Esse campo aceita no máximo 250 caracteres`,
              },
            }}
          />
        </div>
      </div>
      <div className={styles.column}>
        <div className={styles.row}>
          <Select
            label="Tipo"
            name="buildingType"
            options={buildingTypes}
            required
            getLabel={item => item.name}
            getValue={item => item.id}
            defaultValue={getValues('buildingType')}
            disabled={!isEditing}
          />
        </div>
      </div>
      <div className={styles.column}>
        <div className={styles.row}>
          <Select
            label="Ramo de atividade"
            name="businessType"
            options={BUSINESS_TYPE_OPTIONS}
            getLabel={item => item.label}
            getValue={item => item.value}
            defaultValue={getValues('businessType')}
            disabled={!isEditing}
          />
        </div>
      </div>
      <div className={styles.column}>
        <label className={styles.switchLabel} htmlFor="isServiceOrderBuilding">
          Aceitar chamados no empreendimento
          <Tooltip
            arrow
            disableFocusListener
            disableTouchListener
            title={
              <Typography className={styles.tooltipText}>
                Se assinalado, os solicitantes poderão abrir chamados de manutenção na área comum do
                empreendimento.
                <br />
                Se dermarcado, eles só poderão abrir chamados nas unidades do empreendimento.
              </Typography>
            }
            placement="bottom-end"
          >
            <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
          </Tooltip>
        </label>
        <div className={styles.row}>
          <Controller
            name="isServiceOrderBuilding"
            control={control}
            render={({ value, name, onChange }) => {
              return (
                <SwitchComponent
                  name={name}
                  checked={value}
                  onChange={e => onChange(e.target.checked)}
                  disabled={!isEditing}
                />
              )
            }}
          />
        </div>
      </div>
    </Accordion>
  )
}

export const Contact = ({ isEditing }) => {
  const styles = useStyles()
  const { getValues } = useFormContext()
  return (
    <Accordion title="Contato" className={styles.accordion}>
      <div className={styles.column}>
        <div className={styles.row}>
          <BasicInput
            name="responsible"
            label="Responsável"
            labelClasses={styles.radioLabel}
            disabled={!isEditing}
            defaultValue={getValues('responsible')}
            rules={{
              maxLength: {
                value: 200,
                message: `Esse campo aceita no máximo 200 caracteres`,
              },
            }}
          />
        </div>
      </div>
      <div className={styles.column}>
        <div className={styles.row}>
          <MaskBasicInput
            name="phone"
            label="Fone/Celular"
            mask="+55 (99) 9 9999-9999"
            labelClasses={styles.radioLabel}
            disabled={!isEditing}
            rules={{
              pattern: { value: PHONE_REGEX, message: 'Telefone inválido' },
            }}
            defaultValue={getValues('phone')}
          />
        </div>
      </div>
      <div className={styles.column}>
        <div className={styles.row}>
          <BasicInput
            name="emails"
            label="Emails"
            labelClasses={styles.radioLabel}
            disabled={!isEditing}
            defaultValue={getValues('emails')}
            rules={{
              pattern: { value: EMAIL_REGEX, message: 'E-mail inválido' },
            }}
          />
        </div>
      </div>
    </Accordion>
  )
}

export const Information = ({ isEditing }) => {
  const styles = useStyles()
  const { control } = useFormContext()

  return (
    <Accordion title="Outras informações" className={styles.accordion}>
      <div style={{ gridColumn: 'span 5' }}>
        <label className={styles.switchLabel} htmlFor="observations">
          Observações
        </label>
        <div className={styles.row} style={{ width: '100%' }}>
          <Controller
            name="observations"
            control={control}
            render={({ value, name, onChange }) => {
              return (
                <TextField
                  name={name}
                  value={value}
                  minRows={2}
                  fullWidth
                  multiline
                  variant="outlined"
                  InputProps={{ style: { fontSize: '1.4rem', paddingLeft: '8px' } }}
                  className={styles.input}
                  onChange={onChange}
                  disabled={!isEditing}
                />
              )
            }}
          />
        </div>
      </div>
      <div className={styles.column}>
        <label className={styles.switchLabel} htmlFor="active">
          Ativo
        </label>
        <div className={styles.row}>
          <Controller
            name="active"
            control={control}
            render={({ value, name, onChange }) => {
              return (
                <SwitchComponent
                  name={name}
                  checked={value}
                  onChange={e => onChange(e.target.checked)}
                  disabled={!isEditing}
                />
              )
            }}
          />
        </div>
      </div>
    </Accordion>
  )
}

export const BuildingAddress = ({ isEditing }) => {
  const styles = useStyles()
  const { showDialog, closeDialog } = useDialog()

  const { setValue, watch, errors, getValues } = useFormContext()
  const [cepData, setCepData] = useState()

  const stateSelected = watch(STATE)
  const cep = watch(CEP)
  const data = useCepAutoComplete(cep, isEditing)

  const cities = useSelector(getCitiesOptions)
  const stateOptions = useSelector(getStatesOptionsByAcronyms)

  const cityOptions = useMemo(() => {
    if (!stateSelected) {
      return []
    }

    return cities?.filter(c => c.state.uf === stateSelected)
  }, [cities, stateSelected])

  const handleCepDataUpdate = useCallback(() => {
    const address = getValues(ADDRESS)
    if (address !== '' && address !== cepData.address) {
      showDialog({
        type: 'warning',
        subject: 'Você deseja mudar o conteúdo do campo Logradouro pelo novo endereço localizado?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        onApprove: () => {
          setValue('address', cepData.address)
          closeDialog()
        },
        onCancel: closeDialog,
      })
    } else {
      setValue('address', cepData.address)
    }
    setValue('state', cepData.state)

    setValue('neighborhood', cepData.neighborhood)
    setValue('cep', cepData?.cep)
    const findCity = cityOptions.find(city => city?.label === cepData?.city)
    setValue('city', findCity?.label)
  }, [cepData, cityOptions, setValue, getValues])

  useEffect(() => {
    if (cepData?.address) {
      handleCepDataUpdate()
    }
  }, [cepData, handleCepDataUpdate])

  return (
    <Accordion title="Endereço" className={classnames(styles.accordion, styles.accordionGap)}>
      <MaskBasicInput
        label="CEP"
        name="cep"
        mask="99999-999"
        placeholder="00000-000"
        rules={{
          validationCep,
          maxLength: 9,
        }}
        disabled={!isEditing}
        labelClasses={styles.cepLabel}
        onBlur={() => setCepData(data)}
      />
      <BasicInput
        label="Endereço"
        name="address"
        disabled={!isEditing}
        placeholder="Digite o endereço"
      />
      <BasicInput
        label="Número"
        name="number"
        rules={{ maxLength: 64 }}
        disabled={!isEditing}
        placeholder="0000"
      />
      <BasicInput
        label="Complemento"
        name="complement"
        disabled={!isEditing}
        placeholder="Digite o complemento"
      />
      <BasicInput
        label="Bairro"
        name="neighborhood"
        disabled={!isEditing}
        placeholder="Digite o bairro"
      />
      <Grid>
        <Select
          label="Estado"
          name={STATE}
          error={!!errors?.[STATE]}
          options={stateOptions}
          getValue={item => item.value}
          getLabel={item => item.label}
          getKey={item => item.value}
          menuProps={MENU_PROPS}
          containerClasses={styles.select}
        />
      </Grid>
      <Grid>
        <Select
          label="Cidade"
          name={CITY}
          error={!!errors?.[CITY]}
          options={cityOptions}
          getValue={item => item.label}
          getLabel={item => item.label}
          getKey={item => item.value}
          menuProps={MENU_PROPS}
          containerClasses={styles.select}
        />
      </Grid>
    </Accordion>
  )
}

export const Units = ({ buildingType, units, isEditing, onDelete, onSuccess }) => {
  const styles = useStyles()
  const [openModal, setOpenModal] = useState(false)
  const [unitToEdit, setUnitToEdit] = useState({})
  const { showDialog, closeDialog } = useDialog()
  const { showMessage } = useMessage()
  const { id: buildingId } = useParams()

  const handleEdit = useCallback(unit => {
    setUnitToEdit(unit)
    setOpenModal(true)
  }, [])

  const handleAdd = useCallback(() => {
    if (!buildingId) {
      showMessage(5)
      return
    }
    setUnitToEdit({})
    setOpenModal(true)
  }, [buildingId, showMessage])

  const handleDeleteClick = unitObj => {
    showDialog({
      type: 'warning',
      subject: 'Você tem certeza que deseja excluir este registro?',
      labelApprove: 'Sim',
      labelCancel: 'Não',
      onApprove: () => {
        onDelete(unitObj)
        closeDialog()
      },
      onCancel: closeDialog,
    })
  }

  return (
    <Accordion title="Unidades" className={styles.unitsContainer}>
      <Grid className={styles.userList}>
        {units?.map(unit => (
          <Grid
            className={styles.wrapper}
            onClick={() => handleEdit(unit)}
            style={{ cursor: isEditing ? 'pointer' : 'default' }}
          >
            <Grid className={styles.details}>
              <Grid className={[styles.unitDetails, styles.value]}>
                <div className={styles.column}>
                  <Typography className={styles.title} component="p" variant="body1">
                    {buildingType?.reference1Label || '-'}
                  </Typography>
                  <div className={styles.row}>
                    <Typography component="p" variant="body1">
                      {unit?.reference1}
                    </Typography>
                  </div>
                </div>
                <div className={styles.column}>
                  <Typography className={styles.title} component="p" variant="body1">
                    {buildingType?.reference2Label || '-'}
                  </Typography>
                  <div className={styles.row}>
                    <Typography component="p" variant="body1">
                      {unit?.reference2}
                    </Typography>
                  </div>
                </div>
                <div className={styles.column}>
                  <Typography className={styles.title} component="p" variant="body1">
                    Proprietário
                  </Typography>
                  <div className={styles.row}>
                    <Typography component="p" variant="body1">
                      {unit?.owner}
                    </Typography>
                  </div>
                </div>
              </Grid>
              {isEditing && (
                <Grid className={styles.buttonsContainer}>
                  <IconButton
                    icon={Trash}
                    onClick={event => {
                      event.stopPropagation()
                      handleDeleteClick(unit)
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        ))}
      </Grid>
      {isEditing && (
        <Button className={styles.button} variant="outlined" color="primary" onClick={handleAdd}>
          + Adicionar registro
        </Button>
      )}
      <UnitModal
        isOpen={openModal}
        handleClose={setOpenModal}
        unitData={unitToEdit}
        onSuccess={onSuccess}
        units={units}
        acceptsMultipleUnits={buildingType?.acceptsMultipleUnits}
      />
    </Accordion>
  )
}

export const ContactManager = ({ isEditing }) => {
  const styles = useStyles()
  const { getValues } = useFormContext()
  const [cpfCnpjToValidate, setCpfCnpjToValidate] = useState('')

  const methods = useForm({
    mode: 'all',
  })
  const { errors, watch } = methods

  const handleCpfCnpjBlur = useCallback(() => {
    if (errors?.cnpj) return

    const value = watch('cnpj')

    if (value === cpfCnpjToValidate) return

    setCpfCnpjToValidate(value)
  }, [errors?.cnpj, cpfCnpjToValidate])

  return (
    <Accordion title="Síndico" className={styles.accordion}>
      <div className={styles.column}>
        <div className={styles.row}>
          <BasicInput
            name="responsibleManager"
            label="Responsável"
            labelClasses={styles.radioLabel}
            disabled={!isEditing}
            defaultValue={getValues('responsibleManager')}
            rules={{
              maxLength: {
                value: 200,
                message: `Esse campo aceita no máximo 200 caracteres`,
              },
            }}
          />
        </div>
      </div>
      <div className={styles.column}>
        <div className={styles.row}>
          <MaskBasicInput
            name="phoneManager"
            label="Fone/Celular"
            mask="+55 (99) 9 9999-9999"
            labelClasses={styles.radioLabel}
            disabled={!isEditing}
            rules={{
              pattern: { value: PHONE_REGEX, message: 'Telefone inválido' },
            }}
            defaultValue={getValues('phoneManager')}
          />
        </div>
      </div>
      <div className={styles.column}>
        <div className={styles.row}>
          <BasicInput
            name="emailsManager"
            label="Emails"
            labelClasses={styles.radioLabel}
            disabled={!isEditing}
            defaultValue={getValues('emailsManager')}
            rules={{
              pattern: { value: EMAIL_REGEX, message: 'E-mail inválido' },
            }}
          />
        </div>
      </div>
      <div>
        <ControlledCpfCnpjInput
          name="cpfCnpjManager"
          label="CPF/CNPJ"
          showErrorMessage
          errorMessage={errors?.cpfCnpj?.message}
          labelProps={{ className: styles.label }}
          className={styles.cpfCnpjField}
          controllerProps={{
            rules: {
              pattern: {
                value: CPF_CNPJ_REGEX,
                message: 'CPF/CNPJ inválido',
              },
            },
          }}
          fieldProps={{
            error: !!errors?.cnpj,
            disabled: !isEditing,
            onBlur: handleCpfCnpjBlur,
          }}
        />
      </div>
    </Accordion>
  )
}
